import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { auth } from "../firebase";
import { API_BASE_URL } from "../config";
import Sidebar from "./Sidebar";
import CbreApprovalControls from "./CbreApprovalControls";
import { classifyCbreInvoice, classifyCbreLine, cbreGstLabel } from "../services/financeCore/cbreGst";

const CLIENT = {
  name: "CBRE Corporate Outsourcing (Private) Limited",
  ntn: "7146765-0",
  strn: "3277 8761 17367",
  address: "Suite #602, 6th Floor, The Forum, Khayaban-e-Jami, Clifton, Karachi"
};

function normalizeHeader(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function getValue(row, names, fallback = "") {
  const entries = Object.entries(row);
  const found = entries.find(([key]) =>
    names.some((name) => normalizeHeader(key) === normalizeHeader(name))
  );
  return found ? found[1] : fallback;
}

function toNumber(value) {
  if (value == null || value === "") return 0;
  const cleaned = String(value).replace(/,/g, "");
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : 0;
}

function roundAmount(value) {
  return Math.round(toNumber(value));
}

function cleanFileName(value) {
  return String(value || "Invoice").replace(/[\\/:*?"<>|]/g, "_");
}

function groupRows(rows) {
  const grouped = new Map();

  rows.forEach((row) => {
    const invoiceNumber = String(getValue(row, ["Invoice Number", "Invoice No"], "Unknown")).trim() || "Unknown";
    if (!grouped.has(invoiceNumber)) grouped.set(invoiceNumber, []);
    grouped.get(invoiceNumber).push(row);
  });

  return Array.from(grouped.entries()).map(([invoiceNumber, invoiceRows]) => {
    const poNumbers = Array.from(new Set(invoiceRows
      .map((row) => String(getValue(row, ["Ref. P.O No.", "PO No", "P/O No"], "")).trim())
      .filter(Boolean)));
    const lineItems = invoiceRows.map((row, index) => {
      const invoiceAmount = roundAmount(getValue(row, ["Invoice Amount", "Amount"]));
      const salesTax = roundAmount(getValue(row, ["Sales Tax", "Tax"]));
      const totalAmount = roundAmount(getValue(row, ["Total Amount", "Total"]));

      return {
        srNo: index + 1,
        description: String(getValue(row, ["Description"], "")).trim(),
        invoiceAmount,
        salesTax,
        totalAmount: totalAmount || Math.round(invoiceAmount + salesTax)
      };
    });
    const totals = lineItems.reduce((sum, item) => ({
      invoiceAmount: sum.invoiceAmount + item.invoiceAmount,
      salesTax: sum.salesTax + item.salesTax,
      totalAmount: sum.totalAmount + item.totalAmount
    }), { invoiceAmount: 0, salesTax: 0, totalAmount: 0 });

    return {
      invoiceNumber,
      poNumbers,
      lineItems,
      totals,
      rowCount: invoiceRows.length
    };
  });
}

const gstOf = (invoice) => classifyCbreInvoice(invoice.lineItems, invoice.totals);

export default function CBREInvoice() {
  const [fileName, setFileName] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  const totalAmount = useMemo(
    () => invoices.reduce((sum, invoice) => sum + invoice.totals.totalAmount, 0),
    [invoices]
  );

  const format = (value) => Math.round(Number(value || 0)).toLocaleString("en-PK");

  const getToken = async () => {
    const user = auth.currentUser;
    if (!user) {
      alert("Not logged in.");
      return null;
    }
    return user.getIdToken();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

    setFileName(file.name);
    setInvoices(groupRows(rows));
  };

  const downloadPDF = async (invoice) => {
    const token = await getToken();
    if (!token) return null;

    const response = await fetch(`${API_BASE_URL}/invoices/download`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        invoiceType: "cbre",
        invoiceNumber: invoice.invoiceNumber,
        clientName: CLIENT.name,
        clientNTN: CLIENT.ntn,
        clientSTRN: CLIENT.strn,
        clientAddress: CLIENT.address,
        purchaseOrder: invoice.poNumbers.join(", "),
        lineItems: invoice.lineItems,
        summary: invoice.totals,
        grandTotal: invoice.totals.totalAmount
      })
    });

    if (!response.ok) {
      alert("PDF generation failed.");
      return null;
    }

    return response.blob();
  };

  const downloadOne = async (invoice) => {
    const blob = await downloadPDF(invoice);
    if (!blob) return;

    saveAs(blob, `${cleanFileName(invoice.invoiceNumber)}.pdf`);
  };

  const downloadAll = async () => {
    if (!invoices.length) {
      alert("Please upload the CBRE workbook first.");
      return;
    }

    setLoading(true);
    try {
      const zip = new JSZip();

      for (const invoice of invoices) {
        const blob = await downloadPDF(invoice);
        if (blob) zip.file(`${cleanFileName(invoice.invoiceNumber)}.pdf`, blob);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "CBRE_Invoices.zip");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />

      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Client Invoice</div>
            <h1 className="page-title">Create CBRE Invoice</h1>
            <p className="page-copy">
              Upload the CBRE invoice workbook, review invoice groups, and download PDFs through the invoice engine.
            </p>
          </div>
          <div className="metric-card" style={{ minWidth: 240 }}>
            <div className="metric-label">Total Value</div>
            <div className="metric-value">PKR {format(totalAmount)}</div>
            <div className="page-copy" style={{ marginTop: 8 }}>
              {invoices.length} invoice(s)
            </div>
          </div>
        </header>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">CBRE Workbook</h2>
              <p className="page-copy" style={{ marginTop: 4 }}>
                Required columns include Invoice Number, Description, Invoice Amount, Sales Tax, and Total Amount.
              </p>
            </div>
            <span className="badge">XLSX</span>
          </div>
          <div className="panel-body">
            <label htmlFor="cbre-file" className="file-drop">
              <div className="file-title">{fileName || "Choose CBRE invoice workbook"}</div>
              <div className="file-copy">Rows are grouped by Invoice Number.</div>
            </label>
            <input id="cbre-file" type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden-input" />

            <div className="button-row">
              <button type="button" className="btn btn-success" disabled={!invoices.length || loading} onClick={downloadAll}>
                {loading ? "Preparing ZIP..." : "Download All PDFs"}
              </button>
            </div>
          </div>
        </section>

        {invoices.length > 0 && (
          <section className="invoice-list">
            {invoices.map((invoice) => (
              <article className="invoice-card" key={invoice.invoiceNumber}>
                <div className="invoice-card-header">
                  <div>
                    <h3 className="invoice-card-title">{invoice.invoiceNumber}</h3>
                    <span className="badge">{invoice.rowCount} line(s)</span>{" "}
                    <span className={`badge ${gstOf(invoice).type === "UNKNOWN" ? "danger" : ""}`}>{cbreGstLabel(gstOf(invoice))}</span>
                  </div>
                  <button type="button" className="btn btn-primary" onClick={() => downloadOne(invoice)}>
                    Download PDF
                  </button>
                </div>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Invoice Amount</th>
                        <th>Sales Tax</th>
                        <th>GST %</th>
                        <th>Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.lineItems.map((item) => (
                        <tr key={`${invoice.invoiceNumber}-${item.srNo}`}>
                          <td>{item.description}</td>
                          <td>{format(item.invoiceAmount)}</td>
                          <td>{format(item.salesTax)}</td>
                          <td>{classifyCbreLine(item).percent}%</td>
                          <td><strong>{format(item.totalAmount)}</strong></td>
                        </tr>
                      ))}
                      <tr>
                        <td><strong>Total</strong></td>
                        <td><strong>{format(invoice.totals.invoiceAmount)}</strong></td>
                        <td><strong>{format(invoice.totals.salesTax)}</strong></td>
                        <td><strong>{gstOf(invoice).percent}%</strong></td>
                        <td><strong>{format(invoice.totals.totalAmount)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <CbreApprovalControls invoice={invoice} />
              </article>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}
